/**
 * Variant registry — single source of truth for hostname/route → VariantConfig.
 *
 * Resolution order:
 *   1. Explicit `override` argument (DEV-only — the caller, `useVariantStore`,
 *      gates this behind `import.meta.env.DEV` so production never honors a
 *      URL-claimed variant override).
 *   2. Hostname overlay (hosts starting with `dpi.` resolve to `dpi`).
 *   3. Otherwise, route.meta.variantId / route.meta.surveyMode override.
 *   4. Otherwise, the default config applies.
 *
 * Server reconciles the client-claimed variant against `X-Forwarded-Host`,
 * so the registry is advisory for the UI; the server is authoritative.
 */

import type { SurveyMode, VariantConfig, VariantId } from "@src/types/variant";
import type { MetricId } from "@src/types/speedtest.ts";

/** All four metrics — the canonical-host / non-dpi default. */
const ALL_METRICS: ReadonlySet<MetricId> = new Set<MetricId>([
    "ping",
    "jitter",
    "download",
    "upload",
]);

/** dpi-only metric set: speedtest-only deployment with no ping/jitter. */
const DPI_METRICS: ReadonlySet<MetricId> = new Set<MetricId>([
    "download",
    "upload",
]);

/** Default config for the canonical host at `/`. */
const DEFAULT_CONFIG: VariantConfig = {
    id: "default",
    visibleMetrics: ALL_METRICS,
    surveyMode: "chooser",
    showAppNav: true,
};

/** dpi host overlay — applies to any host starting with `dpi.`. */
const DPI_CONFIG: VariantConfig = {
    id: "dpi",
    visibleMetrics: DPI_METRICS,
    surveyMode: "none",
    showAppNav: false,
};

/**
 * True when the hostname matches the dpi overlay.
 *
 * Mirror of /server/src/variants.ts:isDpiHost — keep in sync; covered by
 * src/config/__tests__/isDpiHost-sync-check.test.ts. The two implementations
 * cannot share a single module: the server's tsconfig pins `rootDir` to
 * `src/` while the client uses bundler resolution + a different module
 * target. Per R4 §4.3 this is INTENTIONAL duplication, not drift; the sync
 * test guards against silent divergence on either side.
 */
export function isDpiHost(hostname: string): boolean {
    return typeof hostname === "string" && hostname.startsWith("dpi.");
}

/** Shape of route meta the registry consumes. Both fields optional. */
export interface VariantRouteMeta {
    variantId?: VariantId;
    surveyMode?: SurveyMode;
}

/**
 * The URL `:variant` param literals the parameterized speedtest record
 * accepts (`/:variant(test|school|home)?`). `undefined` is the default/`/`
 * path (no segment). These are the AUTHORITY for variant identity under the
 * route-per-state topology (DDR-AV-ROUTE) — the variant no longer lives in a
 * distinct route NAME (all four collapse to the `speedtest`/`speedtest-result`
 * names) but in this param segment.
 */
export type VariantParam = "test" | "school" | "home";

/**
 * Map a `:variant` URL param to the route-meta pair the registry resolves.
 *
 * The four flat records (`/`, `/test`, `/school`, `/home`) collapsed to ONE
 * parameterized record (DDR-AV-ROUTE), so the per-variant `variantId` /
 * `surveyMode` that used to live in each record's static `meta` is now derived
 * from `route.params.variant` here. Keeping this a PURE function (rather than
 * stamping meta in a navigation guard) lets `useVariantStore` resolve the
 * variant reactively off the param and keeps the dpi router guard param-based
 * without a second source of truth.
 *
 *   - `undefined` (the `/` path, no segment) → the canonical default (chooser).
 *   - `"test"`   → `default` variant, `surveyMode: "none"` (the old `/test`).
 *   - `"school"` → `school` variant + survey.
 *   - `"home"`   → `home` variant + survey.
 *
 * `dpi` is intentionally absent: it is a HOSTNAME overlay (`dpi.` prefix),
 * never a URL segment, and `resolveVariant` resolves it before route meta.
 */
/**
 * Read the `:variant` segment out of a generic route-params record, normalized
 * to a single string (or `undefined` for the bare `/` path).
 *
 * vue-router 5 typed routes give `route.params` a per-record UNION type, so a
 * bare `route.params.variant` read does not compile on the records that have no
 * `variant` param. This helper reads through the GENERIC params shape (a plain
 * `Record<string, string | string[]>`) — the correct type for navigation
 * guards + the variant store, which both read params that may or may not be
 * present on the current record — so the access stays typed without a cast.
 */
export function readVariantParam(
    params:
        | Readonly<Partial<Record<"variant", string | string[]>>>
        | null
        | undefined,
): string | undefined {
    // `route.params` is always present under a live router, but consumers that
    // instantiate `useVariantStore` outside a router context (some unit tests
    // exercise the api/survey stores with a bare `useRoute()` whose `params` is
    // undefined) must not throw — fall through to the default (no variant).
    const raw = params?.variant;
    return Array.isArray(raw) ? raw[0] : (raw ?? undefined);
}

export function variantMetaFromParam(
    param: string | string[] | undefined | null,
): VariantRouteMeta | undefined {
    const value = Array.isArray(param) ? param[0] : param;
    switch (value) {
        case "test":
            return { variantId: "default", surveyMode: "none" };
        case "school":
            return { variantId: "school", surveyMode: "school" };
        case "home":
            return { variantId: "home", surveyMode: "home" };
        default:
            // The `/` path (no segment) carries no variant meta — the
            // registry falls through to DEFAULT_CONFIG (chooser).
            return undefined;
    }
}

/** Resolve a VariantConfig from a hostname + route meta pair. */
export function resolveVariant(input: {
    hostname: string;
    routeMeta?: VariantRouteMeta | null | undefined;
    /**
     * Optional explicit variant override (dev-only). When set to a known
     * VariantId, short-circuits all other resolution steps. The caller
     * (`useVariantStore`) gates this behind `import.meta.env.DEV` so prod
     * builds never honor a URL-claimed variant. Lets `npm run dev` cycle
     * both dpi and non-dpi without needing /etc/hosts entries or a
     * separate dev server.
     */
    override?: VariantId | null | undefined;
}): VariantConfig {
    if (input.override === "dpi") return DPI_CONFIG;
    if (input.override === "default") return DEFAULT_CONFIG;

    if (isDpiHost(input.hostname)) {
        return DPI_CONFIG;
    }

    const meta = input.routeMeta ?? null;
    if (!meta) return DEFAULT_CONFIG;

    const id = meta.variantId ?? DEFAULT_CONFIG.id;
    const surveyMode = meta.surveyMode ?? DEFAULT_CONFIG.surveyMode;

    return {
        id,
        visibleMetrics: ALL_METRICS,
        surveyMode,
        showAppNav: true,
    };
}

/** Convenience for environments that only have a hostname (router guards). */
export function resolveVariantId(
    hostname: string,
    routeMeta?: VariantRouteMeta | null | undefined,
): VariantId {
    return resolveVariant({ hostname, routeMeta }).id;
}
